import React, { useState } from "react";
import { X, Camera, Check, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

export default function PhotographerModal({ open, onClose }) {
    const [step, setStep] = useState("form"); // "form" | "done"
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", password: "" });
    const [loading, setLoading] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/register", { ...form, account_type: "photographer" });
            setStep("done");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Fout");
        }
        setLoading(false);
    };

    const close = () => { setStep("form"); setForm({ name: "", company: "", email: "", phone: "", password: "" }); onClose(); };

    return (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={close} data-testid="photog-modal-overlay">
            <div className="relative bg-[#FFFFFF] border border-zinc-200 rounded-md max-w-xl w-full my-8 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="photog-modal">
                <button onClick={close} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-900 z-10" data-testid="photog-modal-close">
                    <X className="h-5 w-5" />
                </button>
                {step === "form" && (
                    <form onSubmit={submit} className="p-8 lg:p-10" data-testid="photog-form">
                        <div className="h-12 w-12 rounded-sm bg-[#0EA5E9]/15 border border-[#0EA5E9]/40 flex items-center justify-center mb-5">
                            <Camera className="h-5 w-5 text-[#0EA5E9]" />
                        </div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#0EA5E9] mb-2">Fotograaf</div>
                        <h2 className="font-display font-black text-2xl tracking-tighter mb-2 text-zinc-950">Vraag je fotograaf-account aan</h2>
                        <p className="text-xs text-zinc-500 mb-4">Na goedkeuring koppelt Social Factory de juiste makelaars aan je account. Je kan dan fotoreeksen uploaden bij hun projecten en foto's bewerken met de AI-tools.</p>
                        <ul className="space-y-2 mb-6">
                            {["Fotoreeksen uploaden bij projecten van jouw makelaars", "Foto's bewerken met de AI-tools (eigen credits)", "Agenda van geplande shoots"].map((t, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-zinc-600">
                                    <Check className="h-4 w-4 text-[#0EA5E9] flex-shrink-0 mt-0.5" /> {t}
                                </li>
                            ))}
                        </ul>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Naam *</Label>
                                <Input required value={form.name} onChange={set("name")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11" data-testid="photog-name" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Bedrijf / Studio</Label>
                                <Input value={form.company} onChange={set("company")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11" data-testid="photog-company" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Email *</Label>
                                <Input type="email" required value={form.email} onChange={set("email")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11" data-testid="photog-email" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">GSM</Label>
                                <Input value={form.phone} onChange={set("phone")} placeholder="+32..." className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11" data-testid="photog-phone" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Wachtwoord * (min. 6 tekens)</Label>
                                <Input type="password" required minLength={6} value={form.password} onChange={set("password")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11" data-testid="photog-password" />
                            </div>
                        </div>
                        <Button type="submit" disabled={loading} className="w-full mt-8 h-11 bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold tracking-wide rounded-sm active:scale-[0.98]" data-testid="photog-submit">
                            {loading ? "Bezig..." : "Aanvraag versturen"}
                        </Button>
                    </form>
                )}
                {step === "done" && (
                    <div className="p-8 lg:p-10 text-center" data-testid="photog-done">
                        <div className="h-14 w-14 mx-auto rounded-full bg-[#0EA5E9]/15 border border-[#0EA5E9]/40 flex items-center justify-center mb-5">
                            <Clock className="h-6 w-6 text-[#0EA5E9]" />
                        </div>
                        <h2 className="font-display font-black text-2xl tracking-tighter mb-3 text-zinc-950">In afwachting van goedkeuring</h2>
                        <p className="text-sm text-zinc-500 leading-relaxed mb-6">Social Factory beoordeelt je aanvraag en koppelt de juiste makelaars aan je account. Je krijgt een bevestiging per e-mail.</p>
                        <Button onClick={close} className="h-11 px-8 bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold rounded-sm">Sluiten</Button>
                    </div>
                )}
            </div>
        </div>
    );
}
